/**
 * Pure helpers for the portal's listing pages (operators, regions): the URL state every listing
 * shares (view, sort, items per page, page, search), pagination, sorting and filtering. Each
 * listing composes its own filter params on top (`group[]`, `location[]`, GGR range …) — see
 * `stringsParam` / `numberParam` — so the shared rules below are pinned once, by unit tests.
 *
 * URL state follows `usage-overview.ts`: `parseListingSearch` is the router's `validateSearch`,
 * keeps what is valid, drops the rest, never throws, and omits every default — so the bare URL is
 * the default view and a stale or hand-edited link degrades to it instead of an error page.
 */

export type ListingView = 'list' | 'cards';

/**
 * @desc List is the default view — agreed with the client on 14 Sep. Figma still defaults to
 * cards; the decision wins over the file.
 */
export const DEFAULT_VIEW: ListingView = 'list';

/**
 * @desc Items-per-page choices — Figma's Views overlay set (decision D-5). `'all'` shows every
 * match on one page.
 */
export const PER_PAGE_OPTIONS = [6, 12, 24, 48, 'all'] as const;
export type PerPage = (typeof PER_PAGE_OPTIONS)[number];

/** @desc 24 per page by default — the client said Figma's 6 was far too few as a default. */
export const DEFAULT_PER_PAGE: PerPage = 24;

export const PER_PAGE_LABELS: Record<`${PerPage}`, string> = {
  '6': '6 per page',
  '12': '12 per page',
  '24': '24 per page',
  '48': '48 per page',
  all: 'Show all',
};

/** @desc A listing's sort options and its default. */
export type ListingOptions<S extends string> = { sorts: readonly S[]; defaultSort: S };

/** @desc The shared URL state, sparse: a field is present only when it differs from its default. */
export type ListingSearch<S extends string = string> = {
  view?: ListingView;
  sort?: S;
  perPage?: PerPage;
  page?: number;
  q?: string;
};

/** @desc The shared URL state with every default filled in — what a page renders from. */
export type ResolvedListing<S extends string = string> = {
  view: ListingView;
  sort: S;
  perPage: PerPage;
  page: number;
  q: string;
};

/** @desc A search param that may hold one string or several, as an array of non-empty strings. */
export const stringsParam = (value: unknown): string[] => {
  const values = Array.isArray(value) ? value : [value];
  return [
    ...new Set(values.filter((v): v is string => typeof v === 'string' && v.trim().length > 0)),
  ];
};

/** @desc A numeric search param (the router hands back numbers or strings), or `undefined`. */
export const numberParam = (value: unknown): number | undefined => {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : undefined;
};

const parsePerPage = (value: unknown): PerPage | undefined => {
  if (value === 'all') return 'all';
  const n = numberParam(value);
  return PER_PAGE_OPTIONS.find((option) => option === n);
};

/** @desc The router's `validateSearch` for the shared fields. Defaults are omitted. */
export const parseListingSearch = <S extends string>(
  raw: Record<string, unknown>,
  options: ListingOptions<S>,
): ListingSearch<S> => {
  const search: ListingSearch<S> = {};
  if (raw.view === 'cards' || raw.view === 'list') {
    if (raw.view !== DEFAULT_VIEW) search.view = raw.view;
  }
  const sort = options.sorts.find((s) => s === raw.sort);
  if (sort && sort !== options.defaultSort) search.sort = sort;
  const perPage = parsePerPage(raw.perPage);
  if (perPage !== undefined && perPage !== DEFAULT_PER_PAGE) search.perPage = perPage;
  const page = numberParam(raw.page);
  if (page !== undefined && Number.isInteger(page) && page > 1) search.page = page;
  const q = typeof raw.q === 'string' ? raw.q.trim() : '';
  if (q) search.q = q;
  return search;
};

/** @desc Fills every default in, for rendering. */
export const resolveListingSearch = <S extends string>(
  search: ListingSearch<S>,
  options: ListingOptions<S>,
): ResolvedListing<S> => ({
  view: search.view ?? DEFAULT_VIEW,
  sort: search.sort ?? options.defaultSort,
  perPage: search.perPage ?? DEFAULT_PER_PAGE,
  page: search.page ?? 1,
  q: search.q ?? '',
});

/**
 * @desc Turns (partial) listing state back into URL search with every default omitted — the
 * inverse of `resolveListingSearch`, normalised exactly as `parseListingSearch` would.
 */
export const serialiseListingSearch = <S extends string>(
  state: Partial<ResolvedListing<S>> | ListingSearch<S>,
  options: ListingOptions<S>,
): ListingSearch<S> => parseListingSearch(state as Record<string, unknown>, options);

/**
 * @desc Applies a change to the shared fields. Anything other than a page change sends the reader
 * back to page 1 — a page number means nothing once the set or its order has changed.
 */
export const withListingSearch = <S extends string>(
  current: ListingSearch<S>,
  patch: Partial<ResolvedListing<S>>,
  options: ListingOptions<S>,
): ListingSearch<S> => {
  const resetsPage = Object.keys(patch).some((key) => key !== 'page');
  const merged = { ...current, ...patch, ...(resetsPage ? { page: 1 } : {}) };
  return serialiseListingSearch(merged, options);
};

export type Page<T> = {
  items: T[];
  /** @desc The page actually shown — clamped into range. */
  page: number;
  totalPages: number;
  total: number;
  /** @desc 1-based index of the first item shown (0 when there are none). */
  from: number;
  /** @desc 1-based index of the last item shown. */
  to: number;
};

/**
 * @desc One page of `items`. An out-of-range page clamps to the nearest real one, so a stale
 * `page=9` link after filtering shows the last page rather than an empty one.
 */
export const paginate = <T>(items: readonly T[], page: number, perPage: PerPage): Page<T> => {
  const total = items.length;
  const size = perPage === 'all' ? Math.max(1, total) : perPage;
  const totalPages = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, Math.floor(page) || 1), totalPages);
  const start = (current - 1) * size;
  const pageItems = items.slice(start, start + size);
  return {
    items: pageItems,
    page: current,
    totalPages,
    total,
    from: pageItems.length > 0 ? start + 1 : 0,
    to: start + pageItems.length,
  };
};

export type SortValue = string | number | null | undefined;

/**
 * @desc A stable, non-mutating sort by a selected value. Strings compare with en-GB collation,
 * case-insensitively; missing values always sort last, whichever the direction, so "no figure"
 * never tops a "highest first" list.
 */
export const sortBy = <T>(
  items: readonly T[],
  select: (item: T) => SortValue,
  direction: 'asc' | 'desc' = 'asc',
): T[] => {
  const sign = direction === 'asc' ? 1 : -1;
  return items
    .map((item, index) => ({ item, index, value: select(item) }))
    .sort((a, b) => {
      const aMissing = a.value === null || a.value === undefined;
      const bMissing = b.value === null || b.value === undefined;
      if (aMissing || bMissing) {
        return aMissing === bMissing ? a.index - b.index : aMissing ? 1 : -1;
      }
      const order =
        typeof a.value === 'string' && typeof b.value === 'string'
          ? a.value.localeCompare(b.value, 'en-GB', { sensitivity: 'base' })
          : Number(a.value) - Number(b.value);
      return order === 0 ? a.index - b.index : order * sign;
    })
    .map(({ item }) => item);
};

/** @desc A filter that does not apply is `null`/`undefined`/`false` and is skipped. */
export type Predicate<T> = ((item: T) => boolean) | null | undefined | false;

/** @desc Items passing every active predicate (filters intersect). */
export const applyFilters = <T>(
  items: readonly T[],
  predicates: ReadonlyArray<Predicate<T>>,
): T[] => {
  const active = predicates.filter((p): p is (item: T) => boolean => typeof p === 'function');
  return active.length === 0 ? [...items] : items.filter((item) => active.every((p) => p(item)));
};

/** @desc A free-text match: every word of `q` appears in at least one of the fields. */
export const matchesQuery = (q: string, ...fields: ReadonlyArray<string | null | undefined>) => {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = fields.filter(Boolean).join(' ').toLowerCase();
  return words.every((word) => haystack.includes(word));
};
